import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import MenuDropdown from "@/components/MenuDropdown";
import TextField from "@/components/TextField";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { DENIED_REASON_OPTIONS, type ApplicationDeniedReason } from "../../types";
import { UPDATE_SUBMITTED_APPLICATION_MUTATION } from "../mutations";
import { MEMBERSHIP_APPLICATION_QUERY } from "../queries";
import { useMembershipApplicationDenialEmailPreview } from "../hooks/useMembershipApplicationDenialEmailPreview";

interface DenyApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  applicationId: string;
}

interface ReasonOption {
  value: ApplicationDeniedReason;
  label: string;
}

export default function DenyApplicationDialog({ open, onClose, applicationId }: DenyApplicationDialogProps) {
  const { setError, clearError } = useErrorBanner();
  const [reason, setReason] = useState<ReasonOption | undefined>(undefined);
  const [messageToApplicant, setMessageToApplicant] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useMembershipApplicationDenialEmailPreview(applicationId);

  useEffect(() => {
    if (open) {
      setReason(undefined);
      setMessageToApplicant("");
      setInternalNote("");
    } else {
      setPreviewOpen(false);
      resetPreview();
    }
  }, [open, resetPreview]);

  const [denyApplication, { loading }] = useMutation(UPDATE_SUBMITTED_APPLICATION_MUTATION, {
    refetchQueries: [{ query: MEMBERSHIP_APPLICATION_QUERY, variables: { id: applicationId } }],
    onCompleted: (data) => {
      if (data.updateSubmittedApplication.success) {
        clearError();
        showSnackbar("Application denied");
        onClose();
      } else {
        setError(data.updateSubmittedApplication.message);
      }
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  const trimmedMessage = messageToApplicant.trim();
  const trimmedNote = internalNote.trim();
  const canSubmit = !loading && !!reason && trimmedMessage.length > 0 && trimmedNote.length > 0;

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview(messageToApplicant);
  };

  const handleDeny = () => {
    if (!reason) return;

    void denyApplication({
      variables: {
        input: {
          id: applicationId,
          status: "denied",
          deniedReason: reason.value,
          messageToApplicant: trimmedMessage,
          internalNote: trimmedNote
        }
      }
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClosed={onClose}
        headline="Deny Application"
        className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} max-h-[85vh]`}
        hasFormElements
        actions={
          <EmailPreviewModalActions onPreview={handlePreviewEmail} previewDisabled={loading}>
            <Button variant="text" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              onClick={handleDeny}
              disabled={!canSubmit}
              testId="deny-application-submit"
            >
              {loading ? "Denying..." : "Deny Application"}
            </Button>
          </EmailPreviewModalActions>
        }
      >
        <div className="flex flex-col gap-6">
          <div className="text-body-medium text-sys-brand-on-surface-variant">
            <p>Are you sure you wish to deny this application?</p>
            <ul className="list-disc pl-5">
              <li>Provide the applicant with a message about why their application is denied.</li>
              <li>
                The applicant will be informed that their application has been denied via an email and on their account.
              </li>
              <li>The applicant will need to start over with a new application when ready.</li>
            </ul>
          </div>
          <MenuDropdown<ReasonOption>
            label="Reason *"
            options={DENIED_REASON_OPTIONS}
            value={reason}
            onSelect={setReason}
            getLabel={(option) => option.label}
            renderItem={(option) => <div className="text-body-large">{option.label}</div>}
            disabled={loading}
            constrainMenuWidth
          />
          <TextField
            label="Message to Applicant *"
            type="textarea"
            rows={3}
            value={messageToApplicant}
            onChangeText={setMessageToApplicant}
            disabled={loading}
            className="w-full"
          />
          <TextField
            label="Internal Note *"
            type="textarea"
            rows={3}
            value={internalNote}
            onChangeText={setInternalNote}
            disabled={loading}
            className="w-full"
          />
        </div>
      </Dialog>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
